import DualPathwayCards from "@/components/sections/DualPathwayCards";
import ClosingCTA from "@/components/sections/ClosingCTA";

const REVERSAL_CONDITIONS = [
    { t: "PCOS & Hormonal Health", d: "Restoring cycle regularity, reducing androgen excess, improving insulin sensitivity, and addressing fertility readiness with cycle-aware protocols." },
    { t: "Type 2 Diabetes & Prediabetes", d: "HbA1c reduction, fasting glucose stabilisation, weight loss when relevant, and structured de-prescribing under supervision." },
    { t: "Fatty Liver (NAFLD/MASLD)", d: "Hepatic fat reduction via nutrition, movement and sleep; serial imaging and enzyme tracking." },
    { t: "Hypertension", d: "Salt sensitivity, sleep apnoea screening, stress physiology and structured exercise — calibrated to home BP logs." },
    { t: "Dyslipidaemia", d: "ApoB-centred lipid management, dietary lipidology, and pharmacology when biology requires it." },
    { t: "Cardiac Rehabilitation", d: "Post-event recovery, secondary prevention, cardiopulmonary conditioning with clinical oversight." },
];

const LONGEVITY_DOMAINS = [
    { t: "Biomarker Diagnostics", d: "Annual 40+ marker panels including ApoB, Lp(a), hsCRP, fasting insulin, omega index, hormones, micronutrients and continuous glucose." },
    { t: "Cardiorespiratory Fitness", d: "VO₂max benchmarking, zone-2 prescription, strength as a vital sign — programmed week-by-week to your physiology." },
    { t: "Sleep & Circadian Health", d: "Sleep architecture analysis, light hygiene, shift-work mitigation, sleep apnoea screening." },
    { t: "Nutritional Programming", d: "Personalised macros, protein-first frameworks, micronutrient repletion, peri-menopausal optimisation." },
    { t: "Cognitive & Stress Health", d: "HRV-led recovery, structured stress dosing, evidence-based cognitive routines." },
    { t: "Wearable Integration", d: "Oura, Whoop, Apple Watch, Garmin, Libre/Dexcom — synthesised into a single weekly clinical view." },
];

export default function ServicesPage() {
    return (
        <>
            <section className="ihx-warm-surface ihx-grid-overlay">
                <div className="ihx-container py-20 lg:py-28">
                    <span className="ihx-eyebrow">Services</span>
                    <h1 className="ihx-h-display mt-5 max-w-3xl">
                        Two pathways. One <span className="ihx-italic">discipline.</span>
                    </h1>
                    <p className="ihx-lead mt-7 max-w-2xl">
                        Every engagement begins with a deep baseline, follows a
                        structured 12-week intensive, and continues with quarterly or
                        annual recalibration — depending on your pathway.
                    </p>
                </div>
            </section>

            <DualPathwayCards />

            <section style={{ backgroundColor: "hsl(var(--bg-chalk))" }}>
                <div className="ihx-container py-20 lg:py-24">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-4">
                            <span className="ihx-eyebrow">Pathway A · Reversal</span>
                            <h2 className="ihx-h-section mt-5">
                                Conditions we routinely <span className="ihx-italic">reverse.</span>
                            </h2>
                            <p className="ihx-lead mt-6 max-w-md">
                                Reversal here means restoring biomarkers to normative
                                ranges and tapering medication under supervision when
                                appropriate.
                            </p>
                        </div>
                        <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {REVERSAL_CONDITIONS.map((c) => (
                                <div key={c.t} className="ihx-card p-7">
                                    <div className="font-serif text-[19px]">{c.t}</div>
                                    <p className="text-[13.5px] text-[hsl(var(--ink-soft))] mt-2 leading-[1.65]">
                                        {c.d}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="relative" style={{
                background:
                    "radial-gradient(60% 80% at 90% 10%, hsl(var(--bg-mist)) 0%, transparent 55%), hsl(var(--bg-chalk))",
            }}>
                <div className="ihx-container py-20 lg:py-24">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-4">
                            <span className="ihx-eyebrow">Pathway B · Longevity</span>
                            <h2 className="ihx-h-section mt-5">
                                Six domains of <span className="ihx-italic">optimisation.</span>
                            </h2>
                            <p className="ihx-lead mt-6 max-w-md">
                                A continuous, year-long partnership designed for
                                high-performers who want measurable returns on biology.
                            </p>
                        </div>
                        <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {LONGEVITY_DOMAINS.map((c) => (
                                <div key={c.t} className="ihx-card p-7">
                                    <div className="font-serif text-[19px]">{c.t}</div>
                                    <p className="text-[13.5px] text-[hsl(var(--ink-soft))] mt-2 leading-[1.65]">
                                        {c.d}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <ClosingCTA />
        </>
    );
}
